import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { cameras, lineColor } from '../cameraData';
import { downloadStats } from '../downloadCounts';

/**
 * Staff-only download stats: summary cards, a window switcher and one card
 * per camera, ranked, with the bar drawn behind the row.
 *
 * ponytail: no charting library. The question is "which cameras get
 * downloaded", and a proportional bar answers it in one CSS rule -- a chart
 * library would be 60kB to draw 161 rectangles.
 *
 * GitHub only exposes a lifetime counter per asset, so the 30/180-day windows
 * are server-side deltas against stored snapshots (see DownloadStats). Until
 * a snapshot is that old the window says how far back it really reaches.
 */
export default class CameraStatsModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    this.data = null;
    this.range = 'all';
    this.sortBy = 'downloads';
    this.desc = true;
    this.q = '';

    downloadStats().then((data) => {
      this.data = data;
      m.redraw();
    });
  }

  className() {
    return 'CameraStatsModal Modal--large';
  }

  title() {
    return app.translator.trans('acseven-chdk-cameras.forum.stats.title');
  }

  content() {
    if (!this.data) {
      return (
        <div className="Modal-body">
          <LoadingIndicator />
        </div>
      );
    }

    const rows = this.rows();
    const max = rows.reduce((n, r) => Math.max(n, r.downloads), 0) || 1;
    const total = rows.reduce((n, r) => n + r.downloads, 0);
    const active = rows.filter((r) => r.downloads > 0).length;
    const top = rows.reduce((best, r) => (r.downloads > (best ? best.downloads : -1) ? r : best), null);

    return (
      <div className="Modal-body CameraStats">
        <div className="CameraStats-ranges">
          {['all', '30', '180'].map((key) => (
            <Button
              className={'Button Button--link CameraStats-range' + (this.range === key ? ' CameraStats-range--active' : '')}
              onclick={() => (this.range = key)}
            >
              {app.translator.trans('acseven-chdk-cameras.forum.stats.range_' + key)}
            </Button>
          ))}
        </div>

        <div className="CameraStats-cards">
          {this.statCard('total', total.toLocaleString())}
          {this.statCard('active', active + ' / ' + rows.length)}
          {this.statCard('top', top && top.downloads ? top.name : '—')}
        </div>

        <p className="CameraStats-since">{this.sinceLabel()}</p>

        <div className="CameraStats-controls">
          <input
            className="FormControl CameraStats-search"
            type="search"
            placeholder={app.translator.trans('acseven-chdk-cameras.forum.index.search_placeholder')}
            value={this.q}
            oninput={(e) => (this.q = e.target.value)}
          />
          <div className="CameraStats-sorts">
            {this.sortButton('downloads')}
            {this.sortButton('name')}
            {this.sortButton('files')}
          </div>
        </div>

        <div className="CameraStats-list">
          {rows.map((r, i) => (
            <div className={'CameraStats-row' + (r.downloads ? '' : ' CameraStats-row--empty')}>
              <span className="CameraStats-bar" style={{ width: (r.downloads / max) * 100 + '%' }} />
              <span className="CameraStats-rank">{i + 1}</span>
              <span className="CameraStats-line" style={{ background: lineColor(r.line) }}>
                {r.line}
              </span>
              <span className="CameraStats-name">{r.name}</span>
              <span className="CameraStats-files">
                {app.translator.trans('acseven-chdk-cameras.forum.stats.files_count', { count: r.files })}
              </span>
              <span className="CameraStats-value">{r.downloads.toLocaleString()}</span>
            </div>
          ))}
          {rows.length === 0 && (
            <p className="CameraStats-empty">{app.translator.trans('acseven-chdk-cameras.forum.index.no_results')}</p>
          )}
        </div>
      </div>
    );
  }

  statCard(key, value) {
    return (
      <div className="CameraStats-card">
        <div className="CameraStats-cardValue">{value}</div>
        <div className="CameraStats-cardLabel">{app.translator.trans('acseven-chdk-cameras.forum.stats.card_' + key)}</div>
      </div>
    );
  }

  /**
   * Clicking the active key flips direction; clicking another switches to it,
   * descending for numbers and ascending for names -- what you want first in
   * both cases.
   */
  sortButton(key) {
    const active = this.sortBy === key;

    return (
      <Button
        className={'Button Button--link CameraStats-sort' + (active ? ' CameraStats-sort--active' : '')}
        icon={active ? 'fas fa-caret-' + (this.desc ? 'down' : 'up') : true}
        onclick={() => {
          this.desc = active ? !this.desc : key !== 'name';
          this.sortBy = key;
        }}
      >
        {app.translator.trans('acseven-chdk-cameras.forum.stats.col_' + key)}
      </Button>
    );
  }

  /**
   * The window is only as old as the oldest snapshot, and on day one there is
   * none at all. Say which, rather than implying 30 days of data exist.
   */
  sinceLabel() {
    if (this.range === 'all') {
      return app.translator.trans('acseven-chdk-cameras.forum.stats.since_all');
    }

    const win = (this.data.windows || {})[this.range] || {};

    if (!win.from) {
      return app.translator.trans('acseven-chdk-cameras.forum.stats.since_none');
    }

    return app.translator.trans(
      'acseven-chdk-cameras.forum.stats.' + (win.partial ? 'since_partial' : 'since_window'),
      { date: win.from }
    );
  }

  counts() {
    if (this.range === 'all') return this.data.counts || {};

    return ((this.data.windows || {})[this.range] || {}).counts || {};
  }

  rows() {
    const counts = this.counts();
    const q = this.q.trim().toLowerCase();

    const rows = cameras
      .filter((c) => !q || c.name.toLowerCase().includes(q) || c.id.includes(q))
      .map((c) => {
        let downloads = 0;
        let files = 0;

        c.firmware.forEach((fw) => {
          ['full', 'small'].forEach((kind) => {
            if (!fw[kind]) return;
            files++;
            downloads += counts[fw[kind].file] || 0;
          });
        });

        return { id: c.id, name: c.name, line: c.line, files, downloads };
      });

    const dir = this.desc ? -1 : 1;
    const key = this.sortBy;

    return rows.sort((a, b) => dir * (key === 'name' ? a.name.localeCompare(b.name) : a[key] - b[key]));
  }
}
